import { SparklesIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

import ButtonLink from '@/components/links/ButtonLink';

import { VOICE_DESIGN_EXAMPLES } from '@/constants/voiceDesignExamples';

/** localStorage flag the /tts page reads to auto-open the Design a Voice
 * modal (same landing→app handoff pattern as TTS_LANDING_CONTEXT_KEY). */
export const TTS_OPEN_DESIGN_VOICE_KEY = 'ttsOpenDesignVoice';

/**
 * Landing-page section for the Voice Design feature (describe a voice in
 * words → the AI generates three previews → save the one you like).
 * The example prompt and chips are the real ones used in the /tts modal.
 */
export default function VoiceDesignSection() {
  const { t } = useLocale('text-to-speech');

  const featuredPrompt = VOICE_DESIGN_EXAMPLES[0];
  const exampleChips = VOICE_DESIGN_EXAMPLES.slice(1, 5);

  const steps = [
    {
      title: t('Describe it'),
      body: t('Age, accent, tone, pacing — a sentence or two is enough.'),
    },
    {
      title: t('Compare three previews'),
      body: t('The AI generates three takes on your description in seconds.'),
    },
    {
      title: t('Use it anywhere'),
      body: t(
        'Save your favorite and generate speech with it like any other voice.',
      ),
    },
  ];

  return (
    <div className='bg-gray-50 py-24 dark:bg-slate-900 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16'>
          {/* Copy */}
          <div>
            <h2 className='text-base font-semibold leading-7 text-primary-600 dark:text-primary-400'>
              {t('Voice Design')}
            </h2>
            <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              {t("Can't find the right voice? Describe it.")}
            </p>
            <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-slate-400'>
              {t(
                'Type a short description of the voice you have in mind and our AI creates it as a brand-new custom voice — no recordings needed.',
              )}
            </p>
            <ol className='mt-8 space-y-5'>
              {steps.map((step, i) => (
                <li key={i} className='flex gap-4'>
                  <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary-50 text-sm font-semibold text-primary-600 dark:bg-primary-500/10 dark:text-primary-400'>
                    {i + 1}
                  </span>
                  <div>
                    <p className='font-semibold text-gray-900 dark:text-white'>
                      {step.title}
                    </p>
                    <p className='mt-0.5 text-sm leading-6 text-gray-600 dark:text-slate-400'>
                      {step.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
            <div className='mt-10'>
              <ButtonLink
                href='/tts?design=1'
                variant='dark'
                onClick={() => {
                  try {
                    localStorage.setItem(TTS_OPEN_DESIGN_VOICE_KEY, '1');
                  } catch {
                    // localStorage unavailable — the ?design=1 query still works
                  }
                }}
              >
                <SparklesIcon className='mr-2 h-5 w-5' aria-hidden='true' />
                {t('Design a Voice')}
              </ButtonLink>
            </div>
          </div>

          {/* Example prompt card — the real example from the Voice Design modal */}
          <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-800 sm:p-8'>
            <p className='text-sm font-medium text-gray-700 dark:text-slate-300'>
              {t('Describe the voice you need')}
            </p>
            <div className='mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-slate-700 dark:bg-slate-900/50'>
              <p className='text-sm leading-6 text-gray-600 dark:text-slate-400'>
                {featuredPrompt.prompt}
              </p>
            </div>
            <p className='mt-6 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500'>
              {t('Popular starting points')}
            </p>
            <div className='mt-3 flex flex-wrap gap-2'>
              {exampleChips.map((example) => (
                <span
                  key={example.name}
                  className='inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
                >
                  <span aria-hidden='true'>{example.emoji}</span>
                  {t(example.name)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
